import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  UsersIcon,
  PlusIcon,
  ShoppingCartIcon,
  CreditCardIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  ClockIcon,
  MapPinIcon,
  CheckCircleIcon,
  UserPlusIcon,
  ExclamationTriangleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { groupOrdersApi } from '../utils/advancedOrdersApi';
import { showSuccessNotification, showErrorNotification } from './NotificationManager';
import GroupOrderForm from './GroupOrderForm';
import GroupOrderDetails from './GroupOrderDetails';
import GroupOrderCard from './GroupOrderCard';

const GroupOrdersImplementation = () => {
  const [publicOrders, setPublicOrders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [activeTab, setActiveTab] = useState('public'); // public, my-orders
  const [currentUser, setCurrentUser] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Get current user info
    const userData = localStorage.getItem('byteUser');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setCurrentUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }

    // Fetch initial data
    fetchPublicOrders();
    fetchMyOrders();
    fetchRestaurants();
  }, []);

  const fetchPublicOrders = async () => {
    setLoading(true);
    try {
      const response = await groupOrdersApi.getPublicGroupOrders({ limit: 10 });
      if (response.success) {
        setPublicOrders(response.data || []);
      }
    } catch (error) {
      showErrorNotification('Failed to load public group orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyOrders = async () => {
    try {
      const response = await groupOrdersApi.getMyGroupOrders({ limit: 10 });
      if (response.success) {
        setMyOrders(response.data || []);
      }
    } catch (error) {
      showErrorNotification('Failed to load your group orders');
    }
  };

  const fetchRestaurants = async () => {
    try {
      const response = await fetch('https://mongobyte-hc73.onrender.com/api/v1/restaurants');
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setRestaurants(data.data);
      }
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    }
  };

  const handleJoinOrder = async (orderId) => {
    try {
      const response = await groupOrdersApi.joinGroupOrder(orderId);
      if (response.success) {
        showSuccessNotification('Successfully joined group order!');
        fetchPublicOrders();
        fetchMyOrders();
        // Navigate to the order details
        setSelectedOrderId(orderId);
      } else {
        showErrorNotification(response.message || 'Failed to join group order');
      }
    } catch (error) {
      showErrorNotification('Failed to join group order');
    }
  };

  const handlePayOrder = async (orderId) => {
    try {
      const response = await groupOrdersApi.payForGroupOrder(orderId);
      if (response.success) {
        showSuccessNotification('Payment processed successfully!');
        fetchMyOrders();
      } else {
        showErrorNotification(response.message || 'Payment failed');
      }
    } catch (error) {
      showErrorNotification('Payment failed');
    }
  };

  const handleCreateOrder = (orderData) => {
    setShowCreateForm(false);
    fetchPublicOrders();
    fetchMyOrders();
    showSuccessNotification('Group order created successfully!');
  };

  const getTimeLeft = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate - now;
    
    if (diff <= 0) return { text: 'Expired', urgent: true };
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return { text: `${days}d ${hours % 24}h left`, urgent: false };
    }
    
    return { text: `${hours}h ${minutes}m left`, urgent: hours < 2 };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-crust font-display flex items-center gap-3">
            <UsersIcon className="w-8 h-8 text-pepperoni" />
            Group Orders
          </h2>
          <p className="text-olive-light font-sans">Order together with friends and split costs</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowCreateForm(true)}
          className="bg-gradient-to-r from-pepperoni to-primary-800 hover:from-primary-800 hover:to-pepperoni text-accentwhite px-6 py-3 rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          Create Group Order
        </motion.button>
      </div>

      {/* Tab Navigation */}
      <div className="bg-accentwhite rounded-2xl shadow-card border border-secondary-200 overflow-hidden">
        <div className="flex">
          <button
            onClick={() => setActiveTab('public')}
            className={`flex-1 py-4 px-6 font-bold font-display transition-all duration-300 ${
              activeTab === 'public'
                ? 'bg-gradient-to-r from-cheese to-secondary-500 text-crust border-b-2 border-pepperoni'
                : 'text-olive-light hover:text-crust hover:bg-secondary-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <SparklesIcon className="w-4 h-4" />
              Public Orders ({publicOrders.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('my-orders')}
            className={`flex-1 py-4 px-6 font-bold font-display transition-all duration-300 ${
              activeTab === 'my-orders'
                ? 'bg-gradient-to-r from-cheese to-secondary-500 text-crust border-b-2 border-pepperoni'
                : 'text-olive-light hover:text-crust hover:bg-secondary-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <UsersIcon className="w-4 h-4" />
              My Orders ({myOrders.length})
            </div>
          </button>
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'public' && (
            <div className="space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                <h4 className="font-bold text-blue-800 font-display mb-2 flex items-center gap-2">
                  <SparklesIcon className="w-5 h-5" />
                  How Group Ordering Works
                </h4>
                <div className="text-sm text-blue-700 font-sans space-y-1">
                  <p>• Browse public group orders from your university</p>
                  <p>• Join orders that interest you</p>
                  <p>• Select and pay for your meals</p>
                  <p>• Chat with other participants</p>
                  <p>• Get your food delivered together!</p>
                </div>
              </div>

              {/* Public Orders Grid */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pepperoni"></div>
                  <span className="ml-3 text-olive-light font-sans">Loading group orders...</span>
                </div>
              ) : publicOrders.length === 0 ? (
                <div className="bg-accentwhite rounded-2xl shadow-card border border-secondary-200 p-12 text-center">
                  <UsersIcon className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-crust font-display mb-2">
                    No public group orders available
                  </h3>
                  <p className="text-olive-light font-sans mb-6">
                    Be the first to create a group order for your friends!
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowCreateForm(true)}
                    className="bg-pepperoni text-accentwhite px-6 py-3 rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Create Group Order
                  </motion.button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {publicOrders.map((order) => (
                    <GroupOrderCard
                      key={order._id}
                      order={order}
                      onJoin={handleJoinOrder}
                      onView={setSelectedOrderId}
                      currentUser={currentUser}
                      isUserOrder={false}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'my-orders' && (
            <div className="space-y-6">
              {/* My Orders List */}
              {myOrders.length === 0 ? (
                <div className="bg-accentwhite rounded-2xl shadow-card border border-secondary-200 p-12 text-center">
                  <UsersIcon className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-crust font-display mb-2">
                    No group orders yet
                  </h3>
                  <p className="text-olive-light font-sans mb-6">
                    Create or join a group order to see it here!
                  </p>
                  <div className="flex gap-4 justify-center">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowCreateForm(true)}
                      className="bg-pepperoni text-accentwhite px-6 py-3 rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      Create Group Order
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setActiveTab('public')}
                      className="bg-secondary-200 text-olive-light px-6 py-3 rounded-xl font-bold font-display hover:bg-secondary-300 transition-all duration-300"
                    >
                      Browse Public Orders
                    </motion.button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {myOrders.map((order) => (
                    <GroupOrderCard
                      key={order._id}
                      order={order}
                      onView={setSelectedOrderId}
                      onPay={handlePayOrder}
                      currentUser={currentUser}
                      isUserOrder={true}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Create Order Modal */}
      {showCreateForm && (
        <GroupOrderForm
          isOpen={showCreateForm}
          onClose={() => setShowCreateForm(false)}
          onSuccess={handleCreateOrder}
          restaurants={restaurants}
        />
      )}

      {/* Order Details Modal */}
      {selectedOrderId && (
        <GroupOrderDetails
          orderId={selectedOrderId}
          isOpen={!!selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
          onUpdate={() => {
            fetchPublicOrders();
            fetchMyOrders();
          }}
        />
      )}
    </div>
  );
};

export default GroupOrdersImplementation;